import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthProvider';
import {
  fetchCarActivityDetails,
  dismissActivityItem,
  dismissAllActivityItems,
  type ActivityListItem,
} from '@/lib/activity/carActivity';
import { ChevronRight, Loader2, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { getSignedViewUrl } from '@/lib/getSignedViewUrl';

type SectionProps = {
  title: string;
  items: ActivityListItem[];
  onNavigate: (href: string) => void;
  onDismiss: (item: ActivityListItem) => Promise<void>;
  dismissingIds: Set<string>;
  userAvatars: Map<string, string | null>;
  userDisplayNames: Map<string, string>;
};

const Section: React.FC<SectionProps> = ({
  title,
  items,
  onNavigate,
  onDismiss,
  dismissingIds,
  userAvatars,
  userDisplayNames,
}) => {
  const { t } = useI18n();
  if (items.length === 0) return null;
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-gray-800" style={{ fontFamily: 'Lato', fontWeight: 700 }}>
        {title}
      </h3>
      <Card className="rounded-[20px] bg-[#FDFDFD]" style={{ boxShadow: '0px 10px 8px -1px #EEEEEE' }}>
        <CardContent className="p-3">
          <div className="space-y-2">
            {items.map((it) => {
              const isDismissing = dismissingIds.has(it.id);
              const avatarUrl = userAvatars.get(it.userId) || null;
              const displayName = userDisplayNames.get(it.userId) || 'User';
              const initial = displayName[0]?.toUpperCase() || 'U';
              return (
                <div
                  key={it.id}
                  className="w-full flex items-center justify-between gap-3 rounded-xl px-3 py-2 hover:bg-gray-50 transition"
                >
                  <button
                    type="button"
                    onClick={() => onNavigate(it.href)}
                    className="flex-1 flex items-center gap-2 text-left min-w-0"
                  >
                    {/* User avatar */}
                    {avatarUrl ? (
                      <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                        <img
                          src={avatarUrl}
                          alt={displayName}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Fallback to initial if image fails to load
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = `<div class="w-8 h-8 rounded-full bg-[#7c9ffa] flex items-center justify-center text-white text-xs font-semibold">${initial}</div>`;
                            }
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[#7c9ffa] flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                        {initial}
                      </div>
                    )}
                    {/* Blue dot indicator */}
                    <div
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: '#7c9ffa' }}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-gray-900 font-medium truncate">{it.title}</div>
                      <div className="text-xs text-gray-500 truncate">
                        {new Date(it.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDismiss(it);
                    }}
                    disabled={isDismissing}
                    className="p-1 rounded-full hover:bg-gray-200 transition flex-shrink-0 disabled:opacity-50"
                    aria-label={t('activity.page.dismiss') || 'Dismiss'}
                  >
                    {isDismissing ? (
                      <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                    ) : (
                      <X className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ActivityPage: React.FC = () => {
  const { carId } = useParams<{ carId: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(false);
  const [dismissingIds, setDismissingIds] = useState<Set<string>>(new Set());
  const [showDismissAllConfirm, setShowDismissAllConfirm] = useState(false);
  const [details, setDetails] = useState<Awaited<ReturnType<typeof fetchCarActivityDetails>> | null>(null);
  const [userAvatars, setUserAvatars] = useState<Map<string, string | null>>(new Map());
  const [userDisplayNames, setUserDisplayNames] = useState<Map<string, string>>(new Map());
  const fetchedDetailsRef = useRef<string | null>(null); // Track which details we've already fetched avatars for
  const loadingRef = useRef(false); // Prevent concurrent loads

  const loadDetails = async () => {
    if (!carId || !user?.id) return;
    
    // Prevent concurrent loads
    if (loadingRef.current) {
      console.log(`⏭️ [ActivityPage] Load already in progress, skipping...`);
      return;
    }
    
    const userIdShort = user.id.substring(0, 8);
    const carIdShort = carId.substring(0, 8);
    console.log(`🔄 [ActivityPage] User ${userIdShort} loading activity details for car ${carIdShort}...`);
    loadingRef.current = true;
    setLoading(true);
    try {
      const d = await fetchCarActivityDetails(carId, user.id);
      setDetails(d);
      // Reset the fetched details ref when new details are loaded
      fetchedDetailsRef.current = null;
      console.log(`✅ [ActivityPage] User ${userIdShort} activity details loaded successfully`);
    } catch (e) {
      console.error(`❌ [ActivityPage] User ${userIdShort} failed to load activity details:`, e);
      setDetails(null);
      fetchedDetailsRef.current = null;
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  // Load activity details on mount
  useEffect(() => {
    if (carId && user?.id) {
      loadDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carId, user?.id]);

  // Fetch user avatars for all activity items (non-blocking, happens after details load)
  useEffect(() => {
    if (!details || !user) return;

    // Create a unique key for this details object to prevent re-fetching
    const detailsKey = `${details.carId}-${details.issues.length}-${details.tasks.length}-${details.services.length}-${details.expenses.length}-${details.carDetails.length}`;
    
    // Skip if we've already fetched avatars for this exact details set
    if (fetchedDetailsRef.current === detailsKey) {
      return;
    }

    // Mark that we're fetching for this details set
    fetchedDetailsRef.current = detailsKey;

    // Don't block UI - fetch avatars asynchronously
    const fetchAvatars = async () => {
      // Collect all unique user IDs from activity items
      const userIds = new Set<string>();
      details.issues.forEach((item) => userIds.add(item.userId));
      details.tasks.forEach((item) => userIds.add(item.userId));
      details.services.forEach((item) => userIds.add(item.userId));
      details.expenses.forEach((item) => userIds.add(item.userId));
      details.carDetails.forEach((item) => userIds.add(item.userId));

      const userIdsArray = Array.from(userIds);
      if (userIdsArray.length === 0) return;

      console.log(
        `🖼️ [ActivityPage] Fetching avatars for ${userIdsArray.length} users:`,
        userIdsArray.map((id) => id.substring(0, 8))
      );

      const avatarMap = new Map<string, string | null>();

      // Fetch user data from public.users table
      const { data: usersData, error } = await supabase
        .from('users')
        .select('id, custom_avatar_url, email')
        .in('id', userIdsArray);

      if (error) {
        console.error('❌ [ActivityPage] Error fetching user data:', error);
        setUserAvatars(avatarMap);
        return;
      }

      // Fetch display names for other users via Edge Function
      const otherUserIds = userIdsArray.filter((id) => id !== user.id);
      const displayNameMap = new Map<string, string>();

      // For current user, get display name from auth metadata
      if (user) {
        const meta: any = user?.user_metadata || {};
        const displayName =
          meta.display_name ||
          meta.full_name ||
          meta.name ||
          (meta.given_name && meta.family_name ? `${meta.given_name} ${meta.family_name}` : null) ||
          user.email?.split('@')[0] ||
          'User';
        displayNameMap.set(user.id, displayName);
      }

      // Fetch display names for other users
      if (otherUserIds.length > 0) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
            const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

            const response = await fetch(`${supabaseUrl}/functions/v1/get-user-display-names`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.access_token}`,
                apikey: supabaseAnonKey,
              },
              body: JSON.stringify({ userIds: otherUserIds }),
            });

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.displayNames) {
                Object.entries(data.displayNames).forEach(([userId, displayName]) => {
                  displayNameMap.set(userId, displayName as string);
                });
              }
            }
          }
        } catch (error) {
          console.error('❌ [ActivityPage] Error fetching display names:', error);
        }
      }

      // Fetch all avatars in parallel for better performance
      const avatarPromises = userIdsArray.map(async (userId) => {
        const userData = usersData?.find((u) => u.id === userId);
        let avatarUrl: string | null = null;

        // Try custom avatar first
        if (userData?.custom_avatar_url) {
          try {
            const signedUrlInfo = await getSignedViewUrl(userData.custom_avatar_url);
            avatarUrl = signedUrlInfo.url;
            console.log(`✅ [ActivityPage] Loaded custom avatar for user ${userId.substring(0, 8)}`);
          } catch (error) {
            console.warn(
              `⚠️ [ActivityPage] Failed to load custom avatar for user ${userId.substring(0, 8)}:`,
              error
            );
          }
        }

        // If no custom avatar and it's the current user, try Google avatar from metadata
        if (!avatarUrl && userId === user.id) {
          const meta: any = user?.user_metadata || {};
          avatarUrl = meta.avatar_url || meta.picture || null;
          if (avatarUrl) {
            console.log(`✅ [ActivityPage] Using Google avatar for current user`);
          }
        }

        return { userId, avatarUrl };
      });

      // Wait for all avatars to load in parallel
      const avatarResults = await Promise.all(avatarPromises);
      avatarResults.forEach(({ userId, avatarUrl }) => {
        avatarMap.set(userId, avatarUrl);
      });

      setUserDisplayNames(displayNameMap);

      console.log(
        `✅ [ActivityPage] Loaded ${Array.from(avatarMap.values()).filter((v) => v !== null).length} avatars out of ${userIdsArray.length} users`
      );
      setUserAvatars(avatarMap);
    };

    fetchAvatars();
  }, [details, user]);

  const hasAny = useMemo(() => {
    if (!details) return false;
    const c = details.counts;
    return (
      c.issuesNew > 0 ||
      c.tasksUpdated > 0 ||
      c.serviceRecordsUpdated > 0 ||
      c.expensesAdded > 0 ||
      c.carDetailsUpdated > 0
    );
  }, [details]);

  const handleDismissItem = async (item: ActivityListItem) => {
    if (!user?.id) return;
    const userIdShort = user.id.substring(0, 8);
    const itemIdShort = item.id.substring(0, 8);
    console.log(
      `👆 [ActivityPage] User ${userIdShort} clicking dismiss on ${item.activityType} item ${itemIdShort}`
    );
    setDismissingIds((prev) => new Set(prev).add(item.id));
    try {
      await dismissActivityItem(user.id, item.activityType, item.id);
      console.log(`✅ [ActivityPage] User ${userIdShort} dismissed item, reloading...`);
      // Reload details to refresh the list
      await loadDetails();
    } catch (e) {
      console.error(`❌ [ActivityPage] User ${userIdShort} failed to dismiss item:`, e);
    } finally {
      setDismissingIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  const handleDismissAll = async () => {
    if (!carId || !user?.id || !details) return;
    const userIdShort = user.id.substring(0, 8);
    console.log(`👆 [ActivityPage] User ${userIdShort} confirming dismiss all...`);
    setDismissing(true);
    try {
      // Collect all items
      const allItems = [
        ...details.issues.map((item) => ({ type: item.activityType, id: item.id })),
        ...details.tasks.map((item) => ({ type: item.activityType, id: item.id })),
        ...details.services.map((item) => ({ type: item.activityType, id: item.id })),
        ...details.expenses.map((item) => ({ type: item.activityType, id: item.id })),
        ...details.carDetails.map((item) => ({ type: item.activityType, id: item.id })),
      ];

      console.log(
        `🗑️ [ActivityPage] User ${userIdShort} dismissing all ${allItems.length} items: ${details.issues.length} issues, ${details.tasks.length} tasks, ${details.services.length} services, ${details.expenses.length} expenses, ${details.carDetails.length} car details`
      );

      await dismissAllActivityItems(user.id, allItems);
      console.log(`✅ [ActivityPage] User ${userIdShort} dismissed all, navigating home...`);
      navigate('/home');
    } catch (e) {
      console.error(`❌ [ActivityPage] User ${userIdShort} failed to dismiss all:`, e);
    } finally {
      setDismissing(false);
      setShowDismissAllConfirm(false);
    }
  };

  return (
    <PageLayout
      title={t('activity.page.title') || 'New activity'}
      showBackButton
      onBack={() => navigate(-1)}
      className="bg-[#f5f5f5] min-h-screen"
    >
      <div className="max-w-4xl mx-auto p-4 pb-28 space-y-4 min-h-[calc(100vh-4rem)]">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-gray-600">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            <span>{t('common.loading') || 'Loading...'}</span>
          </div>
        ) : !details || !hasAny ? (
          <Card className="rounded-[20px] bg-[#FDFDFD]" style={{ boxShadow: '0px 10px 8px -1px #EEEEEE' }}>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600" style={{ fontFamily: 'Lato' }}>
                {t('activity.page.empty') || 'No new activity.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Section
              title={t('activity.sections.issues') || 'Issues'}
              items={details.issues}
              onNavigate={(href) => navigate(href)}
              onDismiss={handleDismissItem}
              dismissingIds={dismissingIds}
              userAvatars={userAvatars}
              userDisplayNames={userDisplayNames}
            />
            <Section
              title={t('activity.sections.expenses') || 'Expenses'}
              items={details.expenses.map(item => {
                // If the title is the hardcoded "Expense added", translate it
                if (item.title === 'Expense added') {
                  return {
                    ...item,
                    title: t('activity.summary.expenses', { count: 1 }) || 'Expense added'
                  };
                }
                return item;
              })}
              onNavigate={(href) => navigate(href)}
              onDismiss={handleDismissItem}
              dismissingIds={dismissingIds}
              userAvatars={userAvatars}
              userDisplayNames={userDisplayNames}
            />
            <Section
              title={t('activity.sections.services') || 'Service records'}
              items={details.services}
              onNavigate={(href) => navigate(href)}
              onDismiss={handleDismissItem}
              dismissingIds={dismissingIds}
              userAvatars={userAvatars}
              userDisplayNames={userDisplayNames}
            />
            <Section
              title={t('activity.sections.tasks') || 'Tasks'}
              items={details.tasks}
              onNavigate={(href) => navigate(href)}
              onDismiss={handleDismissItem}
              dismissingIds={dismissingIds}
              userAvatars={userAvatars}
              userDisplayNames={userDisplayNames}
            />
            <Section
              title={t('activity.sections.carDetails') || 'Car details'}
              items={details.carDetails.map(item => ({
                ...item,
                title: t('activity.summary.carDetails') || 'Car details updated'
              }))}
              onNavigate={(href) => navigate(href)}
              onDismiss={handleDismissItem}
              dismissingIds={dismissingIds}
              userAvatars={userAvatars}
              userDisplayNames={userDisplayNames}
            />
          </>
        )}
      </div>

      {/* Sticky footer */}
      <div className="fixed left-0 right-0 bottom-16 z-50 border-t border-gray-200 bg-[#f5f5f5]">
        <div className="mx-auto max-w-4xl px-4 py-2 flex gap-2">
          <Button variant="secondary" className="flex-1 text-sm" onClick={() => navigate(-1)} disabled={dismissing}>
            {t('activity.page.close') || (t('actions.close') || 'Close')}
          </Button>
          <Button
            variant="default"
            className="flex-1 bg-[#e7fe52] text-black hover:bg-[#e7fe52]/90 text-sm"
            onClick={() => setShowDismissAllConfirm(true)}
            disabled={dismissing || !hasAny}
          >
            {dismissing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                {t('activity.page.dismissing') || 'Dismissing...'}
              </>
            ) : (
              t('activity.page.markAllRead') || 'Mark all as read'
            )}
          </Button>
        </div>
      </div>

      {/* Confirmation dialog for dismiss all */}
      <AlertDialog open={showDismissAllConfirm} onOpenChange={setShowDismissAllConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('activity.page.dismissAllConfirm') || 'Dismiss all activity?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('activity.page.dismissAllDescription') ||
                "This will dismiss all unread activity items. You can't undo this action."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-row justify-between items-center">
            <AlertDialogCancel>{t('activity.page.cancel') || 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDismissAll}
              className="bg-[#e7fe52] text-black hover:bg-[#e7fe52]/90"
            >
              {t('activity.page.dismissAllButton') || 'Dismiss All'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
};

export default ActivityPage;
